/** Typed view of public/assets/manifest.json written by the pipeline gate (R91, R95). */
import { versionAsset } from "./revision.js";
import { ICON_OVERRIDES } from "./icon-overrides.js";
import { AUTHORED_INTERFACE_ART } from "./authored-interface-art.js";
export type AssetKind = "building" | "unit" | "zombie" | "prop" | "item" | "emote" | "ui" | "terrain" | "scene" | "smoke";

export interface AssetEntry {
  id: string;
  kind: AssetKind;
  /** Default model (buildings: tier 2, zombies: fresh). */
  model?: string;
  lod1?: string;
  /** Buildings: per tier "1"|"2"|"3"; zombies: per variant. */
  models?: Record<string, string>;
  lod1s?: Record<string, string>;
  icon?: string;
  icon1x?: string;
  portrait?: string;
  tierIcons?: Record<string, string>;
  variantIcons?: Record<string, string>;
  /** Authored material textures, keyed by their pipeline variant. */
  textures?: Record<string, string>;
  /** Comic panels rendered from the associated native 3D scene. */
  panels?: { "1x": string; "2x": string };
  panelProvenance?: string;
  tris: number;
  lod1Tris?: number;
  clips: string[];
  hash: string;
  iconCoverage?: number;
  portraitCoverage?: number;
}

export interface AssetManifest {
  version: number;
  generatedAt: string;
  count: number;
  assets: Record<string, AssetEntry>;
}

let cached: Promise<AssetManifest> | null = null;

export function loadManifest(fetchImpl: typeof fetch = fetch): Promise<AssetManifest> {
  if (!cached) {
    cached = fetchImpl(versionAsset("/assets/manifest.json")).then((r) => {
      if (!r.ok) throw new Error(`manifest ${r.status}`);
      return r.json() as Promise<AssetManifest>;
    });
  }
  return cached;
}

export function resetManifestCache(): void {
  cached = null;
}

/** Authored item silhouettes, sealed separately from the Blender catalogue. */
export const AUTHORED_ITEM_ART = {
  "itm.research_badge": "research-badge",
  "itm.merit_order": "merit-order",
  "itm.wild_ticket": "wild-ticket",
  "itm.gacha_key": "gacha-key",
  "itm.vault_key": "vault-key",
  "itm.enhance_core": "enhance-core",
  "itm.chest_s_module": "s-gear-crate",
  "itm.speedup_general_1": "speedup-general",
  "itm.speedup_general_5": "speedup-general",
  "itm.speedup_general_15": "speedup-general",
  "itm.speedup_general_60": "speedup-general",
  "itm.speedup_general_180": "speedup-general",
  "itm.speedup_general_480": "speedup-general",
  "itm.speedup_general_1440": "speedup-general",
  "itm.speedup_general_4320": "speedup-general",
  "itm.speedup_build_1": "speedup-build",
  "itm.speedup_build_5": "speedup-build",
  "itm.speedup_build_15": "speedup-build",
  "itm.speedup_build_60": "speedup-build",
  "itm.speedup_build_180": "speedup-build",
  "itm.speedup_build_480": "speedup-build",
  "itm.speedup_build_1440": "speedup-build",
  "itm.speedup_build_4320": "speedup-build",
  "itm.speedup_research_1": "speedup-research",
  "itm.speedup_research_5": "speedup-research",
  "itm.speedup_research_15": "speedup-research",
  "itm.speedup_research_60": "speedup-research",
  "itm.speedup_research_180": "speedup-research",
  "itm.speedup_research_480": "speedup-research",
  "itm.speedup_research_1440": "speedup-research",
  "itm.speedup_research_4320": "speedup-research",
  "itm.speedup_repair_1": "speedup-repair",
  "itm.speedup_repair_5": "speedup-repair",
  "itm.speedup_repair_15": "speedup-repair",
  "itm.speedup_repair_60": "speedup-repair",
  "itm.speedup_repair_180": "speedup-repair",
  "itm.speedup_repair_480": "speedup-repair",
  "itm.speedup_repair_1440": "speedup-repair",
  "itm.speedup_repair_4320": "speedup-repair",
  "itm.speedup_fabrication_1": "speedup-fabrication",
  "itm.speedup_fabrication_5": "speedup-fabrication",
  "itm.speedup_fabrication_15": "speedup-fabrication",
  "itm.speedup_fabrication_60": "speedup-fabrication",
  "itm.speedup_fabrication_180": "speedup-fabrication",
  "itm.speedup_fabrication_480": "speedup-fabrication",
  "itm.speedup_fabrication_1440": "speedup-fabrication",
  "itm.speedup_fabrication_4320": "speedup-fabrication",
  "itm.crate_rat_1k": "crate-rations",
  "itm.crate_rat_10k": "crate-rations",
  "itm.crate_rat_100k": "crate-rations",
  "itm.crate_rat_1m": "crate-rations",
  "itm.crate_pol_1k": "crate-polymer",
  "itm.crate_pol_10k": "crate-polymer",
  "itm.crate_pol_100k": "crate-polymer",
  "itm.crate_pol_1m": "crate-polymer",
  "itm.crate_aly_1k": "crate-alloy",
  "itm.crate_aly_10k": "crate-alloy",
  "itm.crate_aly_100k": "crate-alloy",
  "itm.crate_aly_1m": "crate-alloy",
  "itm.crate_enc_1k": "crate-energy",
  "itm.crate_enc_10k": "crate-energy",
  "itm.crate_enc_100k": "crate-energy",
  "itm.crate_enc_1m": "crate-energy",
  "itm.xp_500": "training-data",
  "itm.xp_2000": "training-data",
  "itm.xp_10000": "training-data",
  "itm.xp_50000": "training-data",
  "itm.cdc": "ability-core",
  "itm.fab": "fabrication-token",
  "itm.ffab": "spotlight-fabrication-token",
  "itm.stamina_20": "fuel-can",
  "itm.stamina_50": "fuel-can",
  "itm.stamina_100": "fuel-can",
  "itm.shield_8h": "shield-projector",
  "itm.shield_24h": "shield-projector",
  "itm.shield_3d": "shield-projector",
  "itm.shield_7d": "shield-projector",
  "itm.teleport_random": "teleport-random",
  "itm.teleport_advanced": "teleport-targeted",
  "itm.alliance_teleport": "teleport-alliance",
  "itm.fragment": "unit-fragment",
  "itm.nano": "nano-kit",
  "itm.blueprint": "module-blueprint",
  "itm.drone_core": "drone-core",
  "itm.base_skin_fragment": "base-paint-fragment",
  "itm.sweep": "sweep-ticket",
  "itm.chest_bronze": "chest-bronze",
  "itm.chest_silver": "chest-silver",
  "itm.chest_gold": "chest-gold",
  "itm.deco_garden": "deco-garden",
  "itm.deco_banner": "deco-banner",
  "itm.deco_floodlight": "deco-floodlight",
  "itm.deco_floor_tile": "deco-floor-tile",
  "itm.deco_holo_sign": "deco-holo-sign",
  "itm.deco_statue": "deco-statue",
  "itm.deco_elm": "deco-elm",
  "itm.deco_yard_water": "deco-yard-water",
  "itm.deco_yard_supply": "deco-yard-supply",
  "itm.deco_yard_service": "deco-yard-service",
  "itm.ufr_s": "universal-fragment-s",
  "itm.ufr_a": "universal-fragment-a",
  "itm.ufr_b": "universal-fragment-b",
  "itm.ufr_c": "universal-fragment-c",
  "itm.ufr_d": "universal-fragment-d",
  "itm.token_arena": "arena-coin",
  "itm.token_prestige": "prestige-mark",
  "itm.token_salvage": "salvage-token",
  "itm.token_event": "event-token",
  "itm.alliance_coin": "alliance-coin",
  "itm.proto_blueprint": "prototype-blueprint",
  "itm.skin": "armor-paint",
} as const;

export function hasAuthoredItemArt(id: string): boolean { return Object.hasOwn(AUTHORED_ITEM_ART, id.split(":")[0]!); }

/** Reserved for future items that temporarily need an existing picture. */
export const PLACEHOLDER_ITEM_ART: Readonly<Record<string, { "1x": string; "2x": string }>> = {
};

const KIND_PREFIX = /^(bld|zmb|unit|prop|itm)\./;

/** File key of an asset: content ids drop their kind prefix, terrain ids (tile.ash.0, ruin) are used as-is. */
export function assetKey(id: string): string {
  return id.replace(KIND_PREFIX, "");
}

export function iconUrl(id: string, size: "1x" | "2x" = "2x", buildingLevel?: number): string {
  // NODE transport uses its authored cargo drone everywhere, including stock and market rows.
  if (id === "itm.node_drone") return versionAsset("/assets/interface/mobile-v2/node.webp");
  if (id === "itm.omega_core") return versionAsset("/assets/interface/omega-core.webp");
  // Bound fragments/blueprints share the generic item's authored image.
  const base = id.startsWith("itm.") ? id.split(":")[0]! : id;
  const authored = AUTHORED_ITEM_ART[base as keyof typeof AUTHORED_ITEM_ART];
  if (authored) return versionAsset(`/assets/interface/items-v1/${authored}.webp`);
  // Module rows (including NODE listings) use the same mechanism as equipment previews.
  const module = /^mod\.([a-z]+)_(weapon|armor|core|sensor|drive)_[dcbas]$/.exec(base);
  const interfaceId = module ? `ui.module.${module[1]}_${module[2]}` : base;
  const interfaceArt = AUTHORED_INTERFACE_ART[interfaceId];
  if (interfaceArt) return versionAsset(interfaceArt);
  const placeholder = PLACEHOLDER_ITEM_ART[base];
  if (placeholder) return versionAsset(placeholder[size]);
  // An asset whose picture was rendered under another name (D88); without this the URL is a 404.
  const override = ICON_OVERRIDES[base];
  if (override && buildingLevel === undefined) return versionAsset(override[size]);
  const key = assetKey(base).replace(/^deco_elm$/, "grove_elm");
  if (buildingLevel !== undefined && id.startsWith("bld.")) return versionAsset(`/assets/icons/${key}.t${buildingTier(buildingLevel)}.png`);
  return versionAsset(size === "1x" ? `/assets/icons/${key}@1x.png` : `/assets/icons/${key}.png`);
}

export function portraitUrl(unitId: string): string {
  return versionAsset(AUTHORED_INTERFACE_ART[unitId] ?? `/assets/portraits/${unitId.replace("unit.", "")}.png`);
}

export function textureUrl(id: string, variant: string): string {
  return versionAsset(`/assets/textures/${id}/${variant}.png`);
}

export function panelUrl(id: string, size: "1x" | "2x" = "2x"): string {
  return versionAsset(`/assets/panels/${id}${size === "1x" ? "@1x" : ""}.png`);
}

/** Preload actual catalogue imagery through the same URL constructors as the visible components. */
export function decodeAssetImage(id: string, portrait = false): Promise<void> {
  const picture = new Image();
  picture.src = portrait ? portraitUrl(id) : iconUrl(id);
  return picture.decode();
}

/** Model URL for an asset; buildings take the tier (1–3), zombies the variant. */
export function modelUrl(id: string, variant?: number | string, lod: 0 | 1 = 0): string {
  const key = assetKey(id);
  const suffix = lod === 1 ? ".lod1.glb" : ".glb";
  if (id.startsWith("bld.")) return versionAsset(`/assets/models/${key}.t${variant ?? 2}${suffix}`);
  // The two bosses are built in one variant only; asking for "mutated" by level found no file (28.09.2026, raid on the
  // Necrolith: "kann Modell nicht finden").
  if (id.startsWith("zmb.")) return versionAsset(`/assets/models/${key}.${key.startsWith("boss_") ? "fresh" : variant ?? "fresh"}${suffix}`);
  return versionAsset(`/assets/models/${key}${suffix}`);
}

export function buildingTier(level: number): 1 | 2 | 3 {
  return level >= 21 ? 3 : level >= 11 ? 2 : 1;
}
